'use client'

/**
 * TabbedContent — horizontal tabs with mutually exclusive panels.
 *
 * Each tab carries its own heading + body + optional CTA + optional
 * image. Client component (interactive tab state). Consumes Phase B
 * surface + radius tokens.
 */
import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { MARKDOWN_LIST_STYLES } from './markdownListStyles'
import type { ResolvedPhoto } from '@sifly/shared-utils'
import { toneFill, type CtaTone } from '../lib/ctaTone'

type TabbedContentTab = {
  tab_label: string
  heading?: string
  body?: string
  cta?: { label: string; href: string; tone?: CtaTone }
  icon?: string
  media_item_id?: string
}

export type TabbedContentData = {
  tabs: TabbedContentTab[]
}

export function TabbedContentSection({
  data,
  tabIcons,
  tabBodies,
  mediaPhotos,
  onDark,
}: {
  data: TabbedContentData
  /**
   * Tab glyphs, already rendered by the server parent and indexed to `data.tabs`.
   *
   * Deliberately a ReactNode array rather than the `icon` name strings on the
   * tabs themselves: resolving a name here would mean importing SectionIcon,
   * whose `import * as LucideIcons` namespace lookup cannot be tree-shaken. In
   * a client component that pulls the ENTIRE lucide library (1,691 icons,
   * ~141 KB brotli) into the browser bundle for the ~38 icons actually used —
   * and this was the only client component doing it. Rendering server-side and
   * passing the result keeps every lucide name available to dealers at zero
   * client cost.
   */
  tabIcons?: React.ReactNode[]
  /**
   * Tab bodies, already rendered from markdown by the server parent and indexed
   * to `data.tabs`.
   *
   * Same reason as `tabIcons`, different library: calling `<MarkdownRenderer>`
   * here would import react-markdown, and with it remark-gfm, rehype-raw and
   * the whole unified/micromark/parse5 pipeline — ~318 KB raw / ~102 KB brotli
   * of vendor code, downloaded on EVERY storefront page whether or not it has a
   * `tabbed_content` section. All tab bodies are known at render time (only the
   * ACTIVE index is client state), so there is nothing to gain by parsing them
   * in the browser.
   */
  tabBodies?: React.ReactNode[]
  mediaPhotos?: Map<string, ResolvedPhoto | null>
  /** Central darkness flag from the dispatcher (covers surface_source bands). */
  onDark?: boolean
}) {
  const [activeIdx, setActiveIdx] = useState(0)
  // Mirror the `?? tabs[0]` fallback when indexing tabBodies, so a body can
  // never come from a different tab than the heading/CTA beside it.
  const resolvedIdx = data.tabs[activeIdx] ? activeIdx : 0
  const active = data.tabs[activeIdx] ?? data.tabs[0]
  const activePhoto = active?.media_item_id ? mediaPhotos?.get(active.media_item_id) : undefined

  // When a surface override (tone OR source) is set the dispatcher's wrapper
  // paints the band; don't repaint our own canvas over it. The inner panel stays
  // a light card (reads on any band); only the section-level tab labels flip.
  const style = (data as { style?: { surface_source?: string; surface_tone?: string } }).style
  const hasSurfaceOverride = !!(style?.surface_source || style?.surface_tone)
  const inactiveTabColor = onDark
    ? 'color-mix(in oklch, white 80%, transparent)'
    : 'var(--color-text-secondary)'

  return (
    <section
      className="py-14 md:py-16"
      style={hasSurfaceOverride ? undefined : { background: 'var(--color-surface-canvas)' }}
      data-component="TabbedContentSection"
    >
      <div className="max-w-[var(--section-max-width,var(--max-width-page))] mx-auto px-[var(--section-padding-x,1rem)]">
        <div
          role="tablist"
          className="flex flex-wrap justify-center gap-2 mb-8"
          style={{ borderBottom: '1px solid var(--color-border)' }}
        >
          {data.tabs.map((tab, i) => {
            const isActive = i === activeIdx
            return (
              <button
                key={i}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveIdx(i)}
                className="px-5 py-3 font-medium transition-colors inline-flex items-center gap-1.5"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 'var(--text-sm)',
                  letterSpacing: 'var(--text-eyebrow-tracking)',
                  textTransform: 'var(--text-eyebrow-transform)' as React.CSSProperties['textTransform'],
                  color: isActive ? 'var(--color-cta)' : inactiveTabColor,
                  borderBottom: isActive ? '2px solid var(--color-cta)' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                {tabIcons?.[i]}
                {tab.tab_label}
              </button>
            )
          })}
        </div>
        <div
          role="tabpanel"
          className="grid md:grid-cols-2 gap-8 items-center p-6 md:p-8"
          style={{
            background: 'var(--color-surface-card)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card-soft)',
          }}
        >
          <div>
            {active?.heading && (
              <h3
                className="font-semibold mb-3"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 'var(--text-2xl)',
                  lineHeight: 'var(--leading-2xl)',
                  color: 'var(--color-on-surface-card)',
                }}
              >
                {active.heading}
              </h3>
            )}
            {active?.body && (
              <div
                className={MARKDOWN_LIST_STYLES}
                style={{
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--leading-base)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {tabBodies?.[resolvedIdx]}
              </div>
            )}
            {active?.cta && (
              <Link
                href={active.cta.href}
                // A set `tone` names the exact brand fill; the classes replace the
                // inline default rather than layering under it (an inline
                // background would win over the utility class otherwise).
                className={`cta-case inline-flex mt-5 px-5 py-2.5 font-medium ${toneFill(active.cta.tone) ?? ''}`}
                style={{
                  ...(active.cta.tone
                    ? {}
                    : { background: 'var(--color-cta)', color: 'var(--color-cta-text)' }),
                  borderRadius: 'var(--radius-card)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {active.cta.label}
              </Link>
            )}
          </div>
          {activePhoto?.url && (
            <div
              className="relative w-full aspect-[4/3]"
              style={{ borderRadius: 'var(--radius-card)', overflow: 'hidden' }}
            >
              <Image
                src={activePhoto.url}
                alt={activePhoto.alt_text || active?.heading || active?.tab_label || ''}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 600px"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
